import React, { Component, ReactNode, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageLoader } from '@/components/UI/Loader';

interface ErrorBoundaryProps {
  fallback?: ReactNode;
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: unknown) {
    // Intentionally minimal. Hook in logging here if needed.
    // eslint-disable-next-line no-console
    console.error('Unhandled UI error:', error, errorInfo);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { fallback, children } = this.props;

    if (hasError) {
      if (fallback) return fallback;

      return (
        <div className='min-h-[40vh] flex items-center justify-center p-6'>
          <div className='max-w-xl w-full bg-white border border-slate-200 rounded-lg shadow p-6 text-center'>
            <h2 className='text-xl font-semibold text-slate-900'>
              Something went wrong
            </h2>
            <p className='mt-2 text-slate-600'>
              An unexpected error occurred while rendering this section.
            </p>
            {error?.message && (
              <p className='mt-3 text-sm text-slate-500 truncate'>
                Error: {error.message}
              </p>
            )}
            <div className='mt-6 flex items-center justify-center gap-3'>
              <button
                onClick={this.reset}
                className='px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700'
              >
                Try again
              </button>
              <a
                href='/'
                className='px-4 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50'
              >
                Go home
              </a>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export const RouteErrorBoundary: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const fallback = (
    <div className='min-h-[40vh] flex items-center justify-center p-6'>
      <div className='max-w-xl w-full bg-white border border-slate-200 rounded-lg shadow p-6 text-center'>
        <h2 className='text-xl font-semibold text-slate-900'>We hit a snag</h2>
        <p className='mt-2 text-slate-600'>
          There was a problem loading this page. You can try again or return to
          the dashboard.
        </p>
        <div className='mt-6 flex items-center justify-center gap-3'>
          <button
            onClick={() => navigate(0)}
            className='px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700'
          >
            Reload page
          </button>
          <button
            onClick={() => navigate('/')}
            className='px-4 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50'
          >
            Go home
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <ErrorBoundary key={location.pathname} fallback={fallback}>
      <Suspense fallback={<PageLoader text='Loading page...' />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
};
